/**
 * The SIC catalogue - the application's home screen.
 *
 * The design principle from the brief: **the user does not know any SIC codes
 * and must never be required to.** So the page offers several routes to one
 * destination - the shortlist, which carries the whole set into the
 * acquisition dialog in phase 4:
 *
 *   - Search / categories - "my market is roughly this" or "I can describe
 *     what they do" - the primary finder, since most people arrive typing.
 *   - Company lookup      - "I know one company like it"
 *   - Full classification - "I already know the code"
 *
 * These four used to render as four equally-weighted numbered "steps"
 * stacked down the page, which read as a wizard even though a user only
 * ever takes one of them, then as two unevenly-weighted cards (search
 * one big panel, company lookup and the tree tabbed in a smaller one below
 * it). They are now three tabs of a single panel - search, company lookup
 * and the formal tree are all just different questions the user might
 * already know the answer to, so they read as equally-weighted routes to
 * the same destination rather than a primary path and two afterthoughts.
 *
 * All three tab panels stay mounted at all times and are hidden with CSS
 * rather than unmounted on switch, so whatever the user typed into any of
 * them - a search, a company name, an expanded tree branch - is still there
 * when they tab back.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AcquisitionDialog, AcquisitionStarted } from '../components/AcquisitionDialog.jsx';
import ClassificationTree from '../components/ClassificationTree.jsx';
import CompanyLookup from '../components/CompanyLookup.jsx';
import { IconClose, IconPlay, IconSearch, categoryIcon } from '../components/Icons.jsx';
import SicCodeRow from '../components/SicCodeRow.jsx';
import { sicApi } from '../lib/api.js';
import { useShortlist } from '../lib/shortlist-context.jsx';

/** Long enough that typing a word does not fire five searches, short enough to feel live. */
const SEARCH_DEBOUNCE_MS = 220;

/** The three routes to a code - rendered as tabs of one card. */
const FINDER_TABS = [
  {
    id: 'activity',
    label: 'Search by activity',
  },
  {
    id: 'company',
    label: 'Find via a company you know',
    hint: 'See the codes it files, then acquire everything else registered under them',
    tip: "Companies House returns each search hit's SIC codes inline, so this costs one API call and no per-company lookups. A company can file up to four codes.",
  },
  {
    id: 'tree',
    label: 'Browse the full classification',
    hint: 'The official Section → Division → code tree',
    tip: 'SIC 2007 is a hierarchy: 21 lettered sections, 88 two-digit divisions, then groups, classes and the five-digit codes companies actually file. Companies House accepts a condensed subset of it.',
  },
];

export default function CataloguePage() {
  const shortlist = useShortlist();

  const [categories, setCategories] = useState(null);
  const [openCategory, setOpenCategory] = useState(null);
  const [categoryCodes, setCategoryCodes] = useState(null);

  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState(null);

  // Multi-select across whichever list is on screen.
  const [selected, setSelected] = useState(() => new Set());

  const [activeTab, setActiveTab] = useState(FINDER_TABS[0].id);

  // Phase 4. `acquiring` holds the codes the dialog is open for; `started` holds
  // the queued-runs confirmation. Null in both cases means closed.
  const [acquiring, setAcquiring] = useState(null);
  const [started, setStarted] = useState(null);
  const navigate = useNavigate();

  const toggleSelect = useCallback((code) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }, []);

  useEffect(() => {
    sicApi
      .categories()
      .then((data) => setCategories(data.categories ?? []))
      .catch((caught) => setError(caught.message));
  }, []);

  /* --- Search, debounced, with the previous request cancelled --- */
  const abortRef = useRef(null);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length === 0) {
      setResults(null);
      setIsSearching(false);
      return undefined;
    }

    setIsSearching(true);
    const timer = setTimeout(() => {
      // A slow earlier response must never overwrite a newer one.
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      sicApi
        .search(trimmed, { limit: 60, signal: controller.signal })
        .then((data) => {
          setResults(data);
          setError(null);
        })
        .catch((caught) => {
          if (caught.name === 'AbortError') return;
          setError(caught.message);
        })
        .finally(() => {
          if (!controller.signal.aborted) setIsSearching(false);
        });
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [query]);

  const openCategoryDetail = async (slug) => {
    if (openCategory === slug) {
      setOpenCategory(null);
      setCategoryCodes(null);
      return;
    }
    setOpenCategory(slug);
    setCategoryCodes(null);
    try {
      setCategoryCodes(await sicApi.category(slug));
    } catch (caught) {
      setError(caught.message);
    }
  };

  const selectedCodes = useMemo(() => [...selected], [selected]);

  const addSelectedToShortlist = async () => {
    if (selectedCodes.length === 0) return;
    await shortlist.add(selectedCodes, { source: 'manual' });
    setSelected(new Set());
  };

  return (
    <div className="page">
      <div className="page-content">
      {error ? (
        <div className="alert alert-error" style={{ marginBottom: 'var(--sp-5)' }}>
          <span className="alert-body">
            <span className="alert-title">Something went wrong</span>
            {error}
          </span>
        </div>
      ) : null}

      {/* --- The finder: one card, three tabbed routes to a code. All three
          panels stay mounted and are only hidden with CSS, so switching tabs
          never loses a half-typed search, company name or expanded branch. */}
      <section className="card finder-card" aria-label="Find a SIC code">
        <nav className="tabs" aria-label="Ways to find a SIC code">
          {FINDER_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`tab${activeTab === tab.id ? ' is-active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
              aria-selected={activeTab === tab.id}
            >
              {tab.label}
            </button>
          ))}
        </nav>

        <div className="card-body">
          {/* --- Search by activity + category browsing -------------------- */}
          <div
            className={`finder-tab-panel finder-panel-activity${
              activeTab === 'activity' ? ' is-active' : ''
            }`}
          >
            <div className="finder-hero-head">
              <div>
                <h2 className="finder-hero-title" id="finder-hero-title">
                  What does the business do?
                  <span
                    className="info-tip tip tip-below"
                    data-tip="Searches four sources in order: the code itself, the official Companies House wording, the ONS index of ~16,000 everyday activity descriptions, and a hand-written table of the terms the index still misses. Each result says which one matched."
                  >
                    i
                  </span>
                </h2>
                <p className="finder-hero-hint">
                  Search in plain English, or jump straight to a category below - try recruitment,
                  plumber, care home, IT, haulage.
                </p>
              </div>
              <span
                className="badge badge-info tip tip-below tip-left"
                data-tip="The condensed list Companies House validates filings against - 731 codes, not the ~1,000 of full SIC 2007. A filing using a code outside it is rejected."
              >
                731 codes
              </span>
            </div>

            <div className="search-field search-field-lg">
              <span className="search-field-icon">
                {isSearching ? <span className="spinner" /> : <IconSearch />}
              </span>
              <input
                className="input"
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search by activity - e.g. recruitment, plumber, care home, IT, haulage"
                aria-label="Search SIC codes"
              />
              {query ? (
                <button
                  type="button"
                  className="search-field-clear btn btn-ghost btn-icon btn-sm"
                  onClick={() => setQuery('')}
                  aria-label="Clear search"
                >
                  <IconClose />
                </button>
              ) : null}
            </div>

            {results ? (
              results.results.length === 0 ? (
                <div className="empty">
                  <h3 className="empty-title">Nothing matched &ldquo;{results.query}&rdquo;</h3>
                  <p className="empty-description">
                    Try a different word for the same activity, clear the search to browse by
                    category, or switch to the &ldquo;Find via a company&rdquo; tab above - its
                    filed codes are usually the fastest answer.
                  </p>
                </div>
              ) : (
                <>
                  <div className="row row-tight text-xs text-faint">
                    <span>
                      {results.totalMatches} code{results.totalMatches === 1 ? '' : 's'} matched
                      {results.truncated ? `, showing the top ${results.returned}` : ''}
                    </span>
                    {results.aliasOnly ? (
                      <span
                        className="badge badge-info tip"
                        data-tip="A one or two letter query is matched against the curated term list only. A raw substring scan for 'IT' would return furniture, hospital and digital, which is noise, not results."
                      >
                        short query &middot; curated terms only
                      </span>
                    ) : null}
                  </div>

                  <div className="code-list">
                    {results.results.map((result) => (
                      <SicCodeRow
                        key={result.code.code}
                        entry={result.code}
                        reason={result.reason}
                        activities={result.activities}
                        selected={selected.has(result.code.code)}
                        onToggleSelect={toggleSelect}
                        shortlistSource="search"
                        shortlistDetail={`searched "${results.query}"`}
                        acquisitionState={results.acquisitionStates?.[result.code.code]}
                        staleAfterDays={results.staleAfterDays}
                        onAcquire={(code) => setAcquiring([code])}
                      />
                    ))}
                  </div>
                </>
              )
            ) : (
              <div className="finder-categories">
                <div className="field-label">
                  Or browse by category
                  <span
                    className="info-tip tip tip-below"
                    data-tip="These are hand-curated, not the official A-U sections. The official taxonomy files recruitment under 'Administrative and support service activities' and a software house under 'Information and communication' - accurate, but not how anyone describes their own market. Browse the formal tree in the tab above."
                  >
                    i
                  </span>
                </div>
                {!categories ? (
                  <div className="loading-panel">
                    <span className="spinner" />
                    Loading categories
                  </div>
                ) : (
                  <div className="cat-grid">
                    {categories.map((category) => {
                      const Icon = categoryIcon(category.icon);
                      return (
                        <button
                          type="button"
                          key={category.slug}
                          className={`cat-card${openCategory === category.slug ? ' is-active' : ''}`}
                          onClick={() => void openCategoryDetail(category.slug)}
                          title={category.description}
                        >
                          <span
                            className="cat-card-icon"
                            style={{
                              '--cat-tone-soft': `var(--cat-${category.tone}-soft)`,
                              '--cat-tone-ink': `var(--cat-${category.tone}-ink)`,
                            }}
                          >
                            <Icon />
                          </span>
                          <span className="cat-card-name">{category.name}</span>
                          <span className="cat-card-count">{category.codeCount}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {openCategory ? (
              categoryCodes ? (
                <div className="card">
                  <div className="card-header">
                    <span className="card-title">{categoryCodes.name}</span>
                    <span className="card-header-actions">
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() =>
                          void shortlist.add(
                            categoryCodes.codes.map((entry) => entry.code),
                            { source: 'category', sourceDetail: categoryCodes.name },
                          )
                        }
                      >
                        Shortlist all {categoryCodes.codeCount}
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => {
                          setOpenCategory(null);
                          setCategoryCodes(null);
                        }}
                      >
                        Close
                      </button>
                    </span>
                  </div>
                  <div className="card-body">
                    <p className="field-hint">{categoryCodes.description}</p>
                  </div>
                  <div className="code-list">
                    {categoryCodes.codes.map((entry) => (
                      <SicCodeRow
                        key={entry.code}
                        entry={entry}
                        selected={selected.has(entry.code)}
                        onToggleSelect={toggleSelect}
                        shortlistSource="category"
                        shortlistDetail={categoryCodes.name}
                        acquisitionState={categoryCodes.acquisitionStates?.[entry.code]}
                        staleAfterDays={categoryCodes.staleAfterDays}
                        onAcquire={(code) => setAcquiring([code])}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <div className="loading-panel">
                  <span className="spinner" />
                  Loading codes
                </div>
              )
            ) : null}
          </div>

          {/* --- Find via a company you know -------------------------------- */}
          <div className={`finder-tab-panel${activeTab === 'company' ? ' is-active' : ''}`}>
            <p className="field-hint finder-secondary-hint">
              {FINDER_TABS[1].hint}
              <span className="info-tip tip tip-below" data-tip={FINDER_TABS[1].tip}>
                i
              </span>
            </p>
            <CompanyLookup />
          </div>

          {/* --- Browse the full classification ----------------------------- */}
          <div className={`finder-tab-panel${activeTab === 'tree' ? ' is-active' : ''}`}>
            <p className="field-hint finder-secondary-hint">
              {FINDER_TABS[2].hint}
              <span className="info-tip tip tip-below" data-tip={FINDER_TABS[2].tip}>
                i
              </span>
            </p>
            <ClassificationTree
              selected={selected}
              onToggleSelect={toggleSelect}
              onAcquire={(code) => setAcquiring([code])}
            />
          </div>
        </div>
      </section>

      {/* Sticky multi-select bar - appears only once something is ticked. */}
      {selectedCodes.length > 0 ? (
        <div className="action-bar">
          <span className="action-bar-count">
            {selectedCodes.length} code{selectedCodes.length === 1 ? '' : 's'} selected
          </span>
          <button type="button" className="btn btn-primary" onClick={() => void addSelectedToShortlist()}>
            Add to shortlist
          </button>
          <button
            type="button"
            className="btn btn-primary tip"
            onClick={() => setAcquiring(selectedCodes)}
            data-tip="Pages Companies House for every company filing these codes, stores them in the registry, and leaves a permanent record in the acquisition log. You will see the real count and cost before anything starts."
          >
            <IconPlay />
            Acquire
          </button>
          <span className="spacer" />
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => setSelected(new Set())}
          >
            Clear selection
          </button>
        </div>
      ) : null}
      </div>

      {/* Phase 4. The dialog counts before it commits to anything. */}
      {acquiring ? (
        <AcquisitionDialog
          codes={acquiring}
          // Called with the queued runs when Start was pressed, with nothing
          // when the dialog was simply dismissed.
          onClose={(result) => {
            setAcquiring(null);
            if (!result) return;
            setStarted(result);
            setSelected(new Set());
          }}
        />
      ) : null}

      {started ? (
        <AcquisitionStarted
          result={started}
          onClose={() => setStarted(null)}
          onView={() => {
            setStarted(null);
            navigate('/log');
          }}
        />
      ) : null}
    </div>
  );
}
